import type { DocumentCheck } from '../../api';

export interface PageSlot {
	/** The page number on the statement. */
	page: number;
	/** The page in the uploaded file; absent for a missing page. */
	filePage?: number;
	state: 'ok' | 'missing' | 'unreadable';
}

// shortcut: the check gives page numbers only in its reason text ("Pages 3 and 4 ...").
// Read them from there until the policy returns structured page numbers.
const PAGE_LIST = /\bpages?\s+(\d+(?:\s*(?:,\s*and|,|and|&|-|–|to)\s*\d+)*)/gi;

/** Page numbers a reason names, e.g. "Pages 3 and 4 are missing" -> [3, 4]. */
export const pagesNamedIn = (text: string): number[] => {
	const pages = new Set<number>();
	for (const match of text.matchAll(PAGE_LIST)) {
		for (const part of match[1].split(/\s*(?:,\s*and|,|and|&)\s*/)) {
			const [from, to = from] = part.split(/\s*(?:-|–|to)\s*/).map(Number);
			for (let page = from; page <= to && page - from < 100; page += 1) {
				pages.add(page);
			}
		}
	}
	return [...pages].sort((a, b) => a - b);
};

const pagesFor = (check: DocumentCheck | null | undefined, code: string) =>
	check?.status === 'flagged'
		? check.issues
				.filter((issue) => issue.code === code)
				.flatMap((issue) => pagesNamedIn(issue.reason))
		: [];

/** The statement's pages in order: the file's pages, with the missing ones put in their place. */
export const pageSlots = (
	filePages: number,
	check?: DocumentCheck | null,
): PageSlot[] => {
	const missing = new Set(pagesFor(check, 'MISSING_PAGES'));
	const unreadable = new Set(pagesFor(check, 'UNREADABLE'));
	const total = Math.max(filePages + missing.size, ...missing);
	const slots: PageSlot[] = [];
	let filePage = 0;
	for (let page = 1; page <= total; page += 1) {
		if (missing.has(page)) {
			slots.push({ page, state: 'missing' });
		} else if (filePage < filePages) {
			filePage += 1;
			slots.push({
				page,
				filePage,
				state: unreadable.has(page) ? 'unreadable' : 'ok',
			});
		}
	}
	return slots;
};
